/**
 * In-memory verified-brand registry, sourced from the `car_makes` table.
 *
 * Both the API server and the web client populate this on boot — the server
 * by querying Supabase directly during start-up, the client by fetching
 * `GET /api/listings/brands` and seeding it from the response. The validators
 * in `./index` (and the logo helpers in each artifact) consult the registry
 * synchronously, which keeps the public validator entry points
 * (`isVerifiedMake`, `canonicaliseVerifiedMake`, `validateDraft`,
 * `validateForPublish`, `validateForWizardSubmit`) sync — see #814.
 *
 * Empty-registry semantics:
 *   - On the server the registry is always populated before requests are
 *     accepted (the boot step blocks until the load completes), and a
 *     periodic refresh re-queries `car_makes` so admin-approved brands
 *     become valid without a redeploy.
 *   - On the client the registry may be empty for the first few hundred
 *     milliseconds of a session. To avoid spurious "pick a make from the
 *     verified list" errors during that window, the strict-make checks
 *     fail OPEN when the registry is empty (the API still rejects with
 *     `UNVERIFIED_MAKE_ERROR` if the user manages to submit before the
 *     bootstrap finishes — that's the authoritative gate).
 */

export interface VerifiedBrand {
  /** `car_makes.id` — required for persisting `cars.make_id` alongside `cars.make`. */
  id: number;
  /** Display-cased canonical name as stored in `car_makes.canonical_name`. */
  canonical_name: string;
  /**
   * Slug used to resolve the bundled logo asset at
   * `/images/car-logos/{slug}.{png,webp}`. `null` for niche / motorcycle /
   * commercial brands that don't yet ship a logo asset; the CarLogo
   * component falls back to a monogram chip in that case.
   */
  logo_slug: string | null;
}

interface RegistryEntry {
  id: number;
  canonical: string;
  logoSlug: string | null;
}

let entries: RegistryEntry[] = [];
/** Lookup keyed by lower-cased + whitespace-collapsed canonical name. */
let byNorm: Map<string, RegistryEntry> = new Map();
/**
 * Pending brand requests (`car_make_requests` rows with status='pending').
 * Validators treat these as ACCEPTABLE for save / submit (the host's draft
 * can persist + transition to awaiting_approval) but the admin moderate-
 * approve action still requires the brand to be in `entries` (i.e. promoted
 * to `car_makes` first). See `isPendingMakeRequest` and the matching
 * server-side check in routes/admin.ts.
 */
interface PendingEntry {
  /** `car_make_requests.id` — set on `cars.pending_make_request_id`. */
  id: number;
  /** The text the host typed (display name surfaced to the wizard). */
  display: string;
}
let pendingEntries: PendingEntry[] = [];
let pendingByNorm: Map<string, PendingEntry> = new Map();
/**
 * Monotonic version counter — bumped every time `setVerifiedBrands` or
 * `setPendingMakeRequests` runs. UI components (e.g. CarLogo) include this
 * in their useMemo deps so a card mounted *before* the boot fetch resolves
 * recomputes its asset URL once the registry arrives, instead of being
 * stuck on a memoised null. See #814 architect review.
 */
let registryVersion = 0;
const subscribers = new Set<() => void>();

/**
 * Single source of truth for brand-name normalisation. Exported so the API
 * server (POST /listings/brands/request, /admin/brand-requests/:id/decision)
 * uses the exact same dedupe key as the in-memory registry — preventing
 * `car_makes.normalised_name` drift between code paths (#1547).
 */
export function normaliseBrandKey(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[._/'"`!?,:;()[\]{}]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const norm = normaliseBrandKey;

/**
 * Replace the in-memory verified-brand list. Called by the API server on
 * boot and by its periodic refresh loop, by the web client after the
 * `/listings/brands` fetch, and by tests. Calling it again replaces the
 * previous contents wholesale (no merge).
 */
export function setVerifiedBrands(brands: ReadonlyArray<VerifiedBrand>): void {
  const next: RegistryEntry[] = [];
  const map = new Map<string, RegistryEntry>();
  for (const b of brands) {
    if (!b || typeof b.canonical_name !== "string") continue;
    const canonical = b.canonical_name.trim();
    if (!canonical) continue;
    const id = typeof b.id === "number" && Number.isFinite(b.id) ? b.id : 0;
    const entry: RegistryEntry = {
      id,
      canonical,
      logoSlug: typeof b.logo_slug === "string" && b.logo_slug ? b.logo_slug : null,
    };
    next.push(entry);
    map.set(norm(canonical), entry);
  }
  entries = next;
  byNorm = map;
  registryVersion += 1;
  for (const fn of subscribers) {
    try { fn(); } catch { /* subscribers are best-effort UI nudges */ }
  }
}

/**
 * Pending brand request, as exposed by `GET /api/listings/brands` and
 * surfaced in the wizard's "Request a new make" CTA.
 */
export interface PendingMakeRequest {
  id: number;
  /** The text the host typed — used as the display name pre-approval. */
  original_text: string;
  /**
   * Lower-cased + whitespace-collapsed key. Optional on the wire — clients
   * that don't have it pre-computed can omit it and the registry will derive
   * it from `original_text`.
   */
  normalised_name?: string | null;
}

/**
 * Replace the in-memory pending-request list. Mirrors `setVerifiedBrands`
 * shape — the API server seeds this on boot + after every admin decision,
 * the web client seeds it from `/listings/brands?include_pending=1`.
 */
export function setPendingMakeRequests(
  requests: ReadonlyArray<PendingMakeRequest>,
): void {
  const next: PendingEntry[] = [];
  const map = new Map<string, PendingEntry>();
  for (const r of requests) {
    if (!r || typeof r.original_text !== "string") continue;
    const display = r.original_text.trim();
    if (!display) continue;
    const id = typeof r.id === "number" && Number.isFinite(r.id) ? r.id : 0;
    const key = (r.normalised_name && r.normalised_name.trim())
      ? r.normalised_name.trim().toLowerCase()
      : norm(display);
    if (!key) continue;
    const entry: PendingEntry = { id, display };
    next.push(entry);
    map.set(key, entry);
  }
  pendingEntries = next;
  pendingByNorm = map;
  registryVersion += 1;
  for (const fn of subscribers) {
    try { fn(); } catch { /* subscribers are best-effort UI nudges */ }
  }
}

/** Read-only snapshot of the pending-request set. */
export function getPendingMakeRequests(): ReadonlyArray<PendingMakeRequest> {
  return pendingEntries.map((e) => ({ id: e.id, original_text: e.display }));
}

/**
 * Returns the current registry version. Stable until the next
 * `setVerifiedBrands` / `setPendingMakeRequests` call. Use as part of a
 * `useMemo` / `useSyncExternalStore` dependency list so renders that
 * resolved against an empty registry recompute when the bootstrap fetch
 * completes.
 */
export function getRegistryVersion(): number {
  return registryVersion;
}

/**
 * Subscribe to registry changes — invoked AFTER `entries`/`byNorm` are
 * swapped in. Returns an unsubscribe handle. Designed for
 * `useSyncExternalStore` integration in the web client.
 */
export function subscribeToRegistry(fn: () => void): () => void {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}

/** Read-only snapshot of the registry. */
export function getVerifiedBrands(): ReadonlyArray<VerifiedBrand> {
  return entries.map((e) => ({ id: e.id, canonical_name: e.canonical, logo_slug: e.logoSlug }));
}

/** True when the registry has been populated (i.e. boot fetch completed). */
export function isRegistryReady(): boolean {
  return entries.length > 0;
}

function lookup(make: string | null | undefined): RegistryEntry | null {
  if (!make) return null;
  const key = norm(make);
  if (!key) return null;
  return byNorm.get(key) || null;
}

/**
 * STRICT verified-make check used by save endpoints + the wizard. Returns
 * true when `make` matches a registered canonical_name (case- and
 * whitespace-insensitive). Returns true when the registry is empty so the
 * web client can render the wizard before the boot fetch completes — the
 * API server's registry is always populated and serves as the authoritative
 * gate.
 */
export function isVerifiedMakeStrict(make: string | null | undefined): boolean {
  if (entries.length === 0) return true; // fail-open before boot fetch
  return lookup(make) !== null;
}

/**
 * Returns the canonical display name for a verified make, or `null` when
 * the input doesn't resolve. Pre-boot (empty registry) returns the trimmed
 * input so the wizard's draft-save path doesn't blank the user's value.
 */
export function canonicalForMake(make: string | null | undefined): string | null {
  if (!make) return null;
  const trimmed = String(make).trim();
  if (!trimmed) return null;
  if (entries.length === 0) return trimmed; // fail-open before boot fetch
  const hit = lookup(trimmed);
  return hit ? hit.canonical : null;
}

/**
 * Logo slug for a make string, or `null` when the make is unknown / has no
 * bundled logo. Used by the CarLogo component on the web and by the email
 * logo lookup on the server.
 */
export function logoSlugForMake(make: string | null | undefined): string | null {
  const hit = lookup(make);
  return hit ? hit.logoSlug : null;
}

/**
 * `car_makes.id` for a verified make, or `null` when the input doesn't
 * resolve to a registered brand. Used by the listing-save endpoints to
 * persist `cars.make_id` alongside `cars.make` — keeping the FK in sync
 * with the canonical text is what makes `car_makes` a true source of
 * truth (#814).
 */
export function idForMake(make: string | null | undefined): number | null {
  const hit = lookup(make);
  if (!hit) return null;
  return hit.id > 0 ? hit.id : null;
}

function lookupPending(make: string | null | undefined): PendingEntry | null {
  if (!make) return null;
  const key = norm(make);
  if (!key) return null;
  return pendingByNorm.get(key) || null;
}

/**
 * Returns true when `make` matches an ACTIVE `car_make_requests` row
 * (status='pending'). Used by the validators to accept pending makes for
 * save / submit. Independent of `isVerifiedMakeStrict` — verified vs pending
 * are mutually exclusive states (an approved request promotes the brand
 * into `car_makes` and clears its pending entry).
 */
export function isPendingMakeRequest(make: string | null | undefined): boolean {
  return lookupPending(make) !== null;
}

/**
 * `car_make_requests.id` for a make that's awaiting verification, or null.
 * Used by the listing-save endpoints to persist `cars.pending_make_request_id`
 * alongside the requested text.
 */
export function idForPendingMakeRequest(
  make: string | null | undefined,
): number | null {
  const hit = lookupPending(make);
  if (!hit) return null;
  return hit.id > 0 ? hit.id : null;
}

/**
 * Acceptable-for-save check. True when `make` resolves to either a verified
 * brand OR a pending brand request. Pre-boot (empty registry) returns true
 * to mirror `isVerifiedMakeStrict` — the server's registry is the
 * authoritative gate.
 */
export function isAcceptableMake(make: string | null | undefined): boolean {
  if (entries.length === 0 && pendingEntries.length === 0) return true;
  return isVerifiedMakeStrict(make) || isPendingMakeRequest(make);
}

/**
 * Corporate / legal suffixes that crop up in registry-sourced make strings
 * ("STERLING MOTOR CAR", "BMW AG", "Bugatti Automobiles S.A.S.") but never
 * appear in our canonical brand names. Stripped before the fuzzy lookup
 * used by `canonicalizeMake`.
 */
const CORPORATE_SUFFIX_RE =
  /\b(motor cars?|motors?|automobiles?|automobile|company|corp|corporation|inc|incorporated|llc|ltd|limited|gmbh|ag|s\s?p\s?a|s\s?a\s?s|s\s?a|n\s?v|bv|kg|co|co kg|holdings?|group|the)\b/g;

function stripCorporateSuffix(input: string): string {
  let prev = "";
  let cur = input;
  while (cur !== prev) {
    prev = cur;
    cur = cur.replace(CORPORATE_SUFFIX_RE, " ").replace(/\s+/g, " ").trim();
  }
  return cur;
}

export interface CanonicalMake {
  /** Logo slug when the make resolved to a known brand, else null. */
  slug: string | null;
  /**
   * Canonical display name. When the make resolved, this is the value from
   * `car_makes.canonical_name` exactly as stored ("Mercedes-Benz", "BMW").
   * Otherwise it's the cleaned input with corporate suffixes stripped,
   * Title-Cased, falling back to the trimmed input.
   */
  canonical: string;
}

/**
 * Fuzzy canonicaliser used by the AI-enrichment valuation chain and the
 * email-logo lookup. Strips corporate suffixes and falls back to first-word
 * matching against the registry (so "STERLING MOTOR CAR" resolves to
 * "Sterling" and "BMW E30" resolves to "BMW"). Strict callers should use
 * {@link isVerifiedMakeStrict} / {@link canonicalForMake} instead.
 */
export function canonicalizeMake(make: string | null | undefined): CanonicalMake {
  const trimmed = (make || "").trim();
  if (!trimmed) return { slug: null, canonical: "" };

  // 1. Direct strict match on the full input.
  const direct = lookup(trimmed);
  if (direct) return { slug: direct.logoSlug, canonical: direct.canonical };

  // 2. Strip corporate suffixes and retry.
  const stripped = stripCorporateSuffix(norm(trimmed));
  if (stripped) {
    const hit = byNorm.get(stripped);
    if (hit) return { slug: hit.logoSlug, canonical: hit.canonical };

    // 3. First-word fallback (handles "BMW E30", "Ford Mustang GT" → "Ford").
    const parts = stripped.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      const two = byNorm.get(`${parts[0]} ${parts[1]}`);
      if (two) return { slug: two.logoSlug, canonical: two.canonical };
      const one = byNorm.get(parts[0]);
      if (one) return { slug: one.logoSlug, canonical: one.canonical };
    }
  }

  // No match — return a tidied display string so downstream still has
  // something readable to show / re-query the auction API with.
  const display = (stripped || norm(trimmed))
    .split(" ")
    .filter(Boolean)
    .map((p) => (p.length <= 3 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(" ");
  return { slug: null, canonical: display || trimmed };
}
